import { uploadToS3Direct } from '@components/Common/directUpload';
import { parseS3Url } from '@components/Common/resumeUrl';
import { useWorkspaceTab } from '@components/Common/EntityWorkspace/useWorkspaceTab';
import SkeletonLoader from '@components/Common/Skeleton/SkeletonLoader';
import { useActivityRail } from '@components/Common/EntityWorkspace/useActivityRail';
import CRMStatusChip from '@components/Common/CRMStatusChip';
import ConfirmDialog from '@components/Common/ConfirmDialog';
import {
  ActivityRail,
  EntityWorkspace,
  IdentityRail,
  SectionCard,
  WorkspaceField,
  WorkspaceFieldLabel,
} from '@components/Common/EntityWorkspace';
import RecordActivityCard from '@components/Common/EntityWorkspace/RecordActivityCard';
import type { GlanceRow, TimelineEntry, WorkspaceTab } from '@components/Common/EntityWorkspace';
import ErrorMessage from '@components/Common/ErrorMessage';
import FileUpload from '@components/Common/FileUpload/FileUpload';
import NotesSection from '@components/Common/NotesSection';
import SelectFormField from '@components/Common/SelectFormField';
import TopQuestionsCard from '@components/Common/TopQuestionsCard';
import { TextEditor } from '@components/Common/TextEditor/TextEditor';
import { useToast } from '@components/Common/Toast/ToastProvider';
import WorkspaceInput from '@components/Common/WorkspaceInput';
import {
  companyById,
  companyDiscoveryAnswersSave,
  companyUpdate,
  createCompanyNote,
  deleteCompanyNote,
  generateCompanySeoDescription as generateCompanySeoDescriptionMutation,
} from '@constants/graphQLQueries';
import { TOP_QUESTIONS_COMPANY } from '@constants/topQuestions';
import { fetchGraphQLApiData } from '@helpers/graphQLApi';
import ApartmentOutlinedIcon from '@mui/icons-material/ApartmentOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutlined';
import HistoryIcon from '@mui/icons-material/History';
import LinkOutlinedIcon from '@mui/icons-material/LinkOutlined';
import NotesOutlinedIcon from '@mui/icons-material/NotesOutlined';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import SellOutlinedIcon from '@mui/icons-material/SellOutlined';
import VerifiedOutlinedIcon from '@mui/icons-material/VerifiedOutlined';
import WorkOutlineIcon from '@mui/icons-material/WorkOutlineOutlined';
import {
  Box,
  Button,
  Chip,
  FormControlLabel,
  Switch,
  Typography,
} from '@mui/material';
import axios from 'axios';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useCanWriteJobsAndCompanies } from '@components/Common/Utils/roles';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { companyJobsPath } from '@components/Common/Utils/filterLinks';
import { formatDate } from '@helpers/date';
import { sanitizeHtml } from '@helpers/utils';

const TABS: WorkspaceTab[] = [
  { id: 'profile', label: 'Profile', icon: <ApartmentOutlinedIcon /> },
  { id: 'verification', label: 'Verification', icon: <VerifiedOutlinedIcon /> },
  { id: 'seo', label: 'SEO', icon: <SearchOutlinedIcon /> },
  { id: 'jobs', label: 'Jobs', icon: <WorkOutlineIcon /> },
  { id: 'notes', label: 'Notes', icon: <NotesOutlinedIcon /> },
  { id: 'history', label: 'History', icon: <HistoryIcon /> },
];

const STATUS_OPTIONS = [
  { value: 'active', label: 'Active' },
  { value: 'pending', label: 'Pending' },
  { value: 'rejected', label: 'Rejected' },
  // 'declined' hides the company from every public query (CompanyService), so it
  // is the right answer for spam, abuse and duplicates rather than deletion.
  { value: 'declined', label: 'Declined (hidden from search)' },
];

const LOGO_BUCKET = 'trymintly-companylogo';

const initialsOf = (name?: string) =>
  (name || '').split(' ').filter(Boolean).slice(0, 2).map((w) => w[0]?.toUpperCase()).join('') || 'CO';
const fmt = (d?: string) => formatDate(d);

/** Bucket name out of an S3 URL, so an old logo can be removed on replace. */
const bucketOf = (url: string) =>
  url.match(/https:\/\/([^.]+)\.s3\.ap-south-1\.amazonaws\.com/)?.[1] ?? '';

/** Flat editable shape. Mirrors the fields companyUpdate accepts. */
interface Form {
  companyName: string;
  industry: string;
  about: string;
  salesOwnerName: string;
  companyEmail: string;
  phoneNumber: string;
  website: string;
  linkedInURL: string;
  instagramURL: string;
  address: string;
  city: string;
  state: string;
  country: string;
  zipCode: string;
  status: string;
  isVerified: boolean;
  isFeatured: boolean;
  skills: string[];
  benefits: string[];
  tags: string[];
  logoUrl: string;
  metaTitle: string;
  metaUrl: string;
  metaDescription: string;
  metaKeywords: string;
}

const toForm = (c: any): Form => {
  const cd = c.companyDetails ?? {};
  const l = c.links ?? {};
  const m = c.metadata ?? {};
  return {
    companyName: c.companyName ?? '',
    industry: c.industry ?? '',
    about: c.about ?? '',
    salesOwnerName: c.salesOwnerName ?? '',
    companyEmail: cd.email ?? '',
    phoneNumber: cd.phoneNumber ?? '',
    website: l.website ?? '',
    linkedInURL: l.linkedin ?? '',
    instagramURL: l.instagram ?? '',
    address: cd.address ?? '',
    city: cd.city ?? '',
    state: cd.state ?? '',
    country: cd.country ?? '',
    zipCode: cd.zipCode ?? '',
    status: c.status ?? 'active',
    isVerified: !!c.isVerified,
    isFeatured: !!c.isFeatured,
    skills: c.skills ?? [],
    benefits: c.benefits ?? [],
    tags: c.tags ?? [],
    logoUrl: c.logoUrl ?? '',
    metaTitle: m.title ?? '',
    metaUrl: m.url ?? '',
    metaDescription: m.description ?? '',
    metaKeywords: m.keywords ?? '',
  };
};

const CompanyWorkspace: React.FC = () => {
  const { companyId } = useParams<{ companyId: string }>();
  const navigate = useNavigate();
  const { showToast } = useToast();

  const [company, setCompany] = useState<any | null>(null);
  // Marketing reads every company but edits none (owner's brief, 2026-10-19). The BFF and backend refuse the writes too.
  const canWriteCompany = useCanWriteJobsAndCompanies();
  // The five fixed Sales discovery answers. Control Center only -- the backend redacts
  // discoveryAnswers alongside salesOwnerName for every non-admin read.
  const [discovery, setDiscovery] = useState<Array<{ key: string; value: string }>>([]);
  const [savingDiscovery, setSavingDiscovery] = useState(false);
  const [form, setForm] = useState<Form | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /* In the URL, not in state: a tab is a place, and this one was unshareable and did
     not survive a reload. See useWorkspaceTab. */
  const [activeTab, setActiveTab] = useWorkspaceTab(TABS, 'profile');
  const [activityOpen, toggleActivity] = useActivityRail();

  const [editing, setEditing] = useState<string | null>(null);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [logoFile, setLogoFile] = useState<File | ''>('');
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [skillInput, setSkillInput] = useState('');
  const [benefitInput, setBenefitInput] = useState('');

  const load = useCallback(async (id: string) => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await fetchGraphQLApiData(companyById(id));
      if (!data?.getCompanyById) {
        setError('Company not found.');
        return;
      }
      setCompany(data.getCompanyById);
      setForm(toForm(data.getCompanyById));
      setDiscovery(
        (data.getCompanyById.discoveryAnswers ?? []).map((a: { key?: string; value?: string }) => ({
          key: a.key ?? '',
          value: a.value ?? '',
        })),
      );
    } catch {
      setError('Failed to load the company. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (companyId) load(companyId);
  }, [companyId, load]);

  const setField = <K extends keyof Form>(key: K, value: Form[K]) => {
    setForm((p) => (p ? { ...p, [key]: value } : p));
    setDirty(true);
  };

  const startEdit = (id: string) => { setEditing(id); setDirty(false); };
  const cancelEdit = () => {
    if (company) setForm(toForm(company));
    setLogoFile('');
    setEditing(null);
    setDirty(false);
  };

  /**
   * One save path for every section. The logo is uploaded first because the
   * mutation needs its URL, and the previous object is only removed once the
   * replacement is in place.
   */
  const saveSection = async () => {
    if (!form || !companyId) return;
    if (!form.companyName.trim()) {
      showToast('Company name is required', 'error');
      return;
    }
    if (form.zipCode && !/^\d+$/.test(form.zipCode)) {
      showToast('ZIP code must be numeric', 'error');
      return;
    }
    setSaving(true);
    try {
      let logoUrl = form.logoUrl;
      if (logoFile instanceof File) {
        const fileName = `${form.companyName}_${Date.now()}_${logoFile.name}`;
        // Direct-to-S3 presigned POST. The logo bucket is public-read (the policy decides, not the
        // client), so the object URL is public; build it from the server's key, encoded because a
        // company name routinely has spaces.
        const key = await uploadToS3Direct(LOGO_BUCKET, logoFile, { fileName });
        logoUrl = `https://${LOGO_BUCKET}.s3.ap-south-1.amazonaws.com/${encodeURIComponent(key)}`;
      }
      // Drop the old object only when it has actually been replaced or cleared.
      if (company?.logoUrl && company.logoUrl !== logoUrl) {
        // The whole key, decoded, then encoded as one path segment: logo keys are
        // `<uploader>/<uuid>-<name>` now, and the last URL segment alone would drop the prefix.
        const old = parseS3Url(company.logoUrl);
        if (old && bucketOf(company.logoUrl)) {
          await axios
            .delete(`/api/s3/buckets/${old.bucket}/objects/${encodeURIComponent(old.key)}`)
            .catch(() => undefined); // a stale object must not block the save
        }
      }

      await fetchGraphQLApiData(
        companyUpdate(companyId, {
          ...form,
          logoUrl,
          description: form.about,
          metadata: {
            title: form.metaTitle,
            url: form.metaUrl,
            description: form.metaDescription,
            keywords: form.metaKeywords,
            metadata: company?.metadata?.metadata ?? [],
          },
        }),
        { showStatus: true, message: 'Saving company' },
      );
      setLogoFile('');
      setEditing(null);
      setDirty(false);
      await load(companyId);
    } catch {
      showToast('Failed to save the company', 'error');
    } finally {
      setSaving(false);
    }
  };

  // Opens the dialog; the delete itself runs from onConfirm below. This used to be a
  // window.confirm, which is a single OK button for an irreversible action that also
  // orphans records. The popup this workspace replaced did ask you to type the company
  // name, but compared it with a case-sensitive === against an always-enabled button and
  // logged the mismatch to the console, so typing "Elite Jewellery Recruiters" for
  // "Elite jewellery Recruiters" silently did nothing and read as a broken button.
  // ConfirmDialog trims, compares case-insensitively, and genuinely disables Confirm.
  const handleDeleteCompany = () => {
    if (!companyId) return;
    setConfirmDelete(true);
  };

  const confirmDeleteCompany = async () => {
    if (!companyId) return;
    setSaving(true);
    try {
      await fetchGraphQLApiData(
        `mutation { deleteCompanyById(_id: "${companyId}") { _id } }`,
        { showStatus: true, message: 'Deleting company' },
      );
      setConfirmDelete(false);
      showToast('Company deleted', 'success');
      navigate('/companies');
    } catch {
      // Leave the dialog open: closing it on failure looks like the delete worked.
      showToast('Failed to delete the company', 'error');
    } finally {
      setSaving(false);
    }
  };

  const saveDiscovery = async (answers: Array<{ key: string; value: string }>) => {
    if (!companyId) return;
    setSavingDiscovery(true);
    try {
      await fetchGraphQLApiData(companyDiscoveryAnswersSave(companyId, answers), {
        showStatus: true,
        message: 'Top 5 questions saved',
      });
      setDiscovery(answers);
    } catch {
      setError('Failed to save the discovery answers. Please try again.');
    } finally {
      setSavingDiscovery(false);
    }
  };

  const handleAddNote = async (html: string) => {
    if (!html.trim() || !companyId) return;
    try {
      await fetchGraphQLApiData(createCompanyNote(companyId, html.trim()), {
        showStatus: true,
        message: 'Adding note',
      });
      await load(companyId);
    } catch {
      showToast('Failed to save the note', 'error');
    }
  };

  const handleDeleteNote = async (noteId: string) => {
    if (!noteId || !companyId) return;
    try {
      await fetchGraphQLApiData(deleteCompanyNote(companyId, noteId), {
        showStatus: true,
        message: 'Deleting note',
      });
      await load(companyId);
    } catch {
      showToast('Failed to delete the note', 'error');
    }
  };

  const timeline: TimelineEntry[] = useMemo(() => {
    if (!company) return [];
    const t: TimelineEntry[] = [];
    if (company.updatedAt) t.push({ id: 'u', kind: 'status', text: 'Record updated', meta: `${fmt(company.updatedAt)} · ${company.updatedBy || 'System'}` });
    if (company.createdAt) t.push({ id: 'c', kind: 'submitted', text: 'Company added', meta: `${fmt(company.createdAt)} · ${company.createdBy || 'System'}` });
    return t;
  }, [company]);

  /*
   * Above the early returns below, deliberately. Placed after them this is a conditional hook: the
   * first render bails out at `loading` before reaching it and the next does not, which React
   * reports as "Rendered more hooks than during the previous render" and replaces the whole
   * workspace with the error boundary. The e2e smoke matrix caught exactly that.
   */

  /**
   * Ask the backend for a description and put it in the field for review.
   *
   * Nothing is saved here. The operator still has to press Save on the section, which is what
   * stops a stray click replacing copy somebody wrote by hand. A null answer means generation is
   * switched off or the model's text failed the backend's length and style checks, and is reported
   * as "no suggestion" rather than as an error.
   */
  const [generatingSeo, setGeneratingSeo] = React.useState(false);
  const generateSeoDescription = async () => {
    if (!companyId) return;
    setGeneratingSeo(true);
    try {
      const res = await fetchGraphQLApiData(generateCompanySeoDescriptionMutation(companyId));
      const suggestion = res?.data?.generateCompanySeoDescription;
      if (suggestion) setField('metaDescription', suggestion);
    } finally {
      setGeneratingSeo(false);
    }
  };

  if (loading && !company) return <SkeletonLoader variant="workspace" />;
  if (error && !company) return <ErrorMessage page message={error} />;
  if (!company || !form) return null;

  const isEditing = (id: string) => editing === id;
  const input = (key: keyof Form, type?: string) => (
    <WorkspaceInput
      type={type}
      value={form[key] as string}
      onChange={(v) => setField(key, v as Form[typeof key])}
    />
  );

  const location = [form.city, form.state, form.country].filter(Boolean).join(', ') || '-';
  const jobsCount = Array.isArray(company.jobsPosted) ? company.jobsPosted.length : company.jobsPosted ?? 0;

  const glance: GlanceRow[] = [
    { key: 'Industry', value: form.industry || '-' },
    { key: 'Location', value: location },
    { key: 'Open roles', value: String(jobsCount), mono: true },
  ];

  /** Chip list with add/remove, used for skills, benefits and tags. */
  const chipEditor = (
    key: 'skills' | 'benefits' | 'tags',
    value: string,
    setValue: (v: string) => void,
    placeholder: string,
    sectionId: string,
  ) => (
    <Box sx={{ gridColumn: '1 / -1' }}>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: isEditing(sectionId) ? 1 : 0 }}>
        {form[key].length === 0 && <span className="cc-field__value">-</span>}
        {form[key].map((item) => (
          <Chip
            key={item}
            label={item}
            size="small"
            onDelete={isEditing(sectionId) ? () => setField(key, form[key].filter((s) => s !== item)) : undefined}
          />
        ))}
      </Box>
      {isEditing(sectionId) && (
        <Box sx={{ display: 'flex', gap: 1 }}>
          <WorkspaceInput
            placeholder={placeholder}
            value={value}
            onChange={setValue}
            onKeyDown={(e) => {
              if (e.key !== 'Enter') return;
              e.preventDefault();
              const v = value.trim();
              if (v && !form[key].includes(v)) setField(key, [...form[key], v]);
              setValue('');
            }}
          />
          <Button
            size="small"
            onClick={() => {
              const v = value.trim();
              if (v && !form[key].includes(v)) setField(key, [...form[key], v]);
              setValue('');
            }}
          >
            Add
          </Button>
        </Box>
      )}
    </Box>
  );

  const identityRail = (
    <IdentityRail
      initials={initialsOf(form.companyName)}
      name={form.companyName || 'Company'}
      subId={company._id || companyId || ''}
      statusChip={form.status ? <CRMStatusChip status={form.status} /> : undefined}
      glance={glance}
      audit={{
        createdAt: company.createdAt,
        createdBy: company.createdBy,
        updatedAt: company.updatedAt,
        updatedBy: company.updatedBy,
      }}
    />
  );

  const profile = (
    <>
      <SectionCard
        title="Company profile"
        icon={<ApartmentOutlinedIcon />}
        meta="7 fields"
        editing={isEditing('profile')}
        onEdit={canWriteCompany ? () => startEdit('profile') : undefined}
        onCancel={cancelEdit}
        onSave={saveSection}
      >
        <WorkspaceField label="Company name" value={form.companyName} editing={isEditing('profile')} edit={input('companyName')} />
        <WorkspaceField label="Industry" value={form.industry} editing={isEditing('profile')} edit={input('industry')} />
        <WorkspaceField label="Email" value={form.companyEmail} mono editing={isEditing('profile')} edit={input('companyEmail')} />
        <WorkspaceField label="Phone" value={form.phoneNumber} mono editing={isEditing('profile')} edit={input('phoneNumber')} />
        <WorkspaceField label="HQ" value={location} />
        <WorkspaceField label="Website" value={form.website} mono editing={isEditing('profile')} edit={input('website')} />
        {/* Internal only: who brought this client. Redacted from every public/employer read. */}
        <WorkspaceField label="Sales owner (brought by)" value={form.salesOwnerName || '-'} editing={isEditing('profile')} edit={input('salesOwnerName')} />
      </SectionCard>

      <SectionCard
        title="Address"
        icon={<PlaceOutlinedIcon />}
        meta="5 fields"
        editing={isEditing('address')}
        onEdit={canWriteCompany ? () => startEdit('address') : undefined}
        onCancel={cancelEdit}
        onSave={saveSection}
      >
        <WorkspaceField label="Address" full value={form.address} editing={isEditing('address')} edit={input('address')} />
        <WorkspaceField label="City" value={form.city} editing={isEditing('address')} edit={input('city')} />
        <WorkspaceField label="State" value={form.state} editing={isEditing('address')} edit={input('state')} />
        <WorkspaceField label="Country" value={form.country} editing={isEditing('address')} edit={input('country')} />
        <WorkspaceField label="ZIP code" value={form.zipCode} mono editing={isEditing('address')} edit={input('zipCode')} />
      </SectionCard>

      <SectionCard
        title="About"
        icon={<ApartmentOutlinedIcon />}
        editing={isEditing('about')}
        onEdit={canWriteCompany ? () => startEdit('about') : undefined}
        onCancel={cancelEdit}
        onSave={saveSection}
      >
        {isEditing('about') ? (
          <Box sx={{ gridColumn: '1 / -1' }}>
            <TextEditor value={form.about} setValue={(v: string) => setField('about', v)} />
          </Box>
        ) : (
          <WorkspaceField
            label="About"
            full
            value={
              form.about
                ? <span className="cc-rich" dangerouslySetInnerHTML={{ __html: sanitizeHtml(form.about) }} />
                : '-'
            }
          />
        )}
      </SectionCard>

      <SectionCard
        title="Social links"
        icon={<LinkOutlinedIcon />}
        meta="3 fields"
        editing={isEditing('links')}
        onEdit={canWriteCompany ? () => startEdit('links') : undefined}
        onCancel={cancelEdit}
        onSave={saveSection}
      >
        <WorkspaceField label="Website" full mono value={form.website} editing={isEditing('links')} edit={input('website')} />
        <WorkspaceField label="LinkedIn" full mono value={form.linkedInURL} editing={isEditing('links')} edit={input('linkedInURL')} />
        <WorkspaceField label="Instagram" full mono value={form.instagramURL} editing={isEditing('links')} edit={input('instagramURL')} />
      </SectionCard>

      <SectionCard
        title="Logo"
        icon={<ApartmentOutlinedIcon />}
        editing={isEditing('logo')}
        onEdit={canWriteCompany ? () => startEdit('logo') : undefined}
        onCancel={cancelEdit}
        onSave={saveSection}
      >
        <Box sx={{ gridColumn: '1 / -1' }}>
          {form.logoUrl && !isEditing('logo') && (
            <img
              src={form.logoUrl}
              alt={form.companyName}
              style={{ maxHeight: 64, maxWidth: '100%', objectFit: 'contain' }}
            />
          )}
          {!form.logoUrl && !isEditing('logo') && <span className="cc-field__value">No logo</span>}
          {isEditing('logo') && (
            <>
              <FileUpload
                disabled={false}
                file={logoFile}
                maxFileSize={2 * 1024 * 1024}
                supportedFileTypes=".png,.jpeg,.jpg"
                title="Upload company logo"
                onFileDrop={(f: File) => { setLogoFile(f); setDirty(true); }}
              />
              {form.logoUrl && (
                <Button
                  size="small"
                  color="error"
                  sx={{ mt: 1 }}
                  onClick={() => { setField('logoUrl', ''); setLogoFile(''); }}
                >
                  Remove current logo
                </Button>
              )}
            </>
          )}
        </Box>
      </SectionCard>

      <SectionCard
        title="Skills & benefits"
        icon={<SellOutlinedIcon />}
        meta={`${form.skills.length} skills · ${form.benefits.length} benefits`}
        editing={isEditing('tags')}
        onEdit={canWriteCompany ? () => startEdit('tags') : undefined}
        onCancel={cancelEdit}
        onSave={saveSection}
      >
        <Box sx={{ gridColumn: '1 / -1' }}>
          <WorkspaceFieldLabel>Skills</WorkspaceFieldLabel>
        </Box>
        {chipEditor('skills', skillInput, setSkillInput, 'Add a skill and press Enter', 'tags')}
        <Box sx={{ gridColumn: '1 / -1', mt: 1 }}>
          <WorkspaceFieldLabel>Benefits</WorkspaceFieldLabel>
        </Box>
        {chipEditor('benefits', benefitInput, setBenefitInput, 'Add a benefit and press Enter', 'tags')}
      </SectionCard>
      <TopQuestionsCard
        title="Top 5 Questions"
        questions={TOP_QUESTIONS_COMPANY}
        answers={discovery}
        onSave={saveDiscovery}
        saving={savingDiscovery}
        disabled={!canWriteCompany}
      />
    </>
  );

  const verification = (
    <>
      <SectionCard
        title="Verification"
        icon={<VerifiedOutlinedIcon />}
        meta="3 fields"
        editing={isEditing('verification')}
        onEdit={canWriteCompany ? () => startEdit('verification') : undefined}
        onCancel={cancelEdit}
        onSave={saveSection}
      >
        {isEditing('verification') ? (
          <>
            {/* Spans the row so it sits on its own line, but the control itself is capped:
                a short status select does not need the width of the card. */}
            <Box sx={{ gridColumn: '1 / -1' }} className="cc-field-row">
              <Box className="cc-field-row__grow">
                <SelectFormField
                  required={false}
                  label="Approval status"
                  name="status"
                  value={form.status}
                  options={STATUS_OPTIONS}
                  onChange={(_n, v) => setField('status', String(v))}
                />
              </Box>
            </Box>
            <FormControlLabel
              control={<Switch checked={form.isVerified} onChange={(e) => setField('isVerified', e.target.checked)} />}
              label="Verified"
            />
            <FormControlLabel
              control={<Switch checked={form.isFeatured} onChange={(e) => setField('isFeatured', e.target.checked)} />}
              label="Featured"
            />
          </>
        ) : (
          <>
            <WorkspaceField label="Approval status" value={<CRMStatusChip status={form.status} />} />
            <WorkspaceField label="Verified" value={form.isVerified ? 'Yes' : 'No'} />
            <WorkspaceField label="Featured" value={form.isFeatured ? 'Yes' : 'No'} />
          </>
        )}
      </SectionCard>

      {canWriteCompany && (
      <SectionCard title="Danger zone" icon={<DeleteOutlineIcon />}>
        <Box sx={{ gridColumn: '1 / -1' }}>
          <Button variant="outlined" color="error" size="small" onClick={handleDeleteCompany}>
            Delete company
          </Button>
          <Typography className="cc-field__value" sx={{ mt: 1, color: 'var(--tx-low)' }}>
            Permanent. Set the status to Declined instead to hide it from public search while
            keeping the record and its jobs intact.
          </Typography>
        </Box>
      </SectionCard>
      )}
    </>
  );

  const seo = (
    <SectionCard
      title="SEO metadata"
      icon={<SearchOutlinedIcon />}
      meta="4 fields"
      editing={isEditing('seo')}
      onEdit={canWriteCompany ? () => startEdit('seo') : undefined}
      onCancel={cancelEdit}
      onSave={saveSection}
    >
      <WorkspaceField label="Meta title" full value={form.metaTitle} editing={isEditing('seo')} edit={input('metaTitle')} />
      <WorkspaceField label="Canonical URL" full mono value={form.metaUrl} editing={isEditing('seo')} edit={input('metaUrl')} />
      <WorkspaceField label="Meta description" full value={form.metaDescription} editing={isEditing('seo')} edit={input('metaDescription')} />
      {isEditing('seo') && (
        <Button size="small" disabled={generatingSeo} onClick={generateSeoDescription}>
          {generatingSeo ? 'Generating...' : 'Generate with AI'}
        </Button>
      )}
      <WorkspaceField label="Keywords" full value={form.metaKeywords} editing={isEditing('seo')} edit={input('metaKeywords')} />
    </SectionCard>
  );

  const tabContent: Record<string, React.ReactNode> = {
    profile,
    verification,
    seo,
    jobs: (
      <SectionCard title="Jobs" icon={<WorkOutlineIcon />} meta={`${jobsCount} roles`}>
        <WorkspaceField
          label="Jobs"
          full
          value={
            /*
             * Link, not <a href>. A raw href does a full document load, which throws away the
             * SPA history stack -- so going back to a filtered list came back to an unfiltered
             * one, which is what "the state isn't applied" was describing.
             *
             * The URL was /jobs?company=<name>, a parameter no page reads. The Jobs list filters
             * on filter_<Column>, so it opened completely unfiltered while the URL looked like it
             * had asked for something.
             */
            <Link to={companyJobsPath(companyId || '')} className="cc-link">
              View this company&apos;s jobs
            </Link>
          }
        />
      </SectionCard>
    ),
    notes: (
      <SectionCard title="Notes" icon={<NotesOutlinedIcon />} meta={`${(company.notes ?? []).length} notes`}>
        <Box sx={{ gridColumn: '1 / -1' }}>
          <NotesSection
            notes={company.notes ?? []}
            isEditMode={canWriteCompany}
            onAdd={handleAddNote}
            onDelete={handleDeleteNote}
          />
        </Box>
      </SectionCard>
    ),
    history: (
      <>
        <SectionCard title="Record activity" icon={<HistoryIcon />}>
          <WorkspaceField label="Added" value={`${fmt(company.createdAt)} · ${company.createdBy || 'System'}`} full />
          <WorkspaceField label="Last updated" value={`${fmt(company.updatedAt)} · ${company.updatedBy || 'System'}`} full />
        </SectionCard>
        <RecordActivityCard recordId={companyId} />
      </>
    ),
  };

  return (
    <>
      <EntityWorkspace
        backLabel="Companies"
        onBack={() => navigate('/companies')}
        identityRail={identityRail}
        tabs={TABS}
        activeTabId={activeTab}
        onTabChange={setActiveTab}
        activityRail={<ActivityRail timeline={timeline} />}
        activityOpen={activityOpen}
        onToggleActivity={toggleActivity}
        isDirty={dirty && !!editing}
        dirtySectionLabel={editing ?? ''}
        onDiscard={cancelEdit}
        onSave={saving ? undefined : saveSection}
      >
        {tabContent[activeTab]}
      </EntityWorkspace>

      <ConfirmDialog
        open={confirmDelete}
        title="Delete this company?"
        body={(
          <>
            This permanently removes {form?.companyName || 'this company'}. Its jobs and the
            employer accounts attached to it are not deleted and will be left orphaned. To take
            a company out of public view instead, cancel and set its status to Declined.
          </>
        )}
        confirmText={form?.companyName || 'DELETE'}
        confirmLabel={form?.companyName ? 'Company name' : 'Type DELETE'}
        actionLabel="Delete company"
        busy={saving}
        onCancel={() => setConfirmDelete(false)}
        onConfirm={confirmDeleteCompany}
      />
    </>
  );
};

export default CompanyWorkspace;
